import React, { useState, useRef, useEffect, useCallback, useMemo, useContext } from 'react';
import { ZoomIn, ZoomOut, Maximize2, Minimize2 } from 'lucide-react';
import { SidebarContext } from '../../contexts/SidebarContext';

// Flow-board view of the Contact to Build SOP: numbered step cards laid out in a
// serpentine grid, connected by orange arrows, on a pannable canvas.
// Steps come from the same source the table modal uses, so tailored accounts see
// their own flow. Scrolling pans vertically; zoom is button-only; the expand
// button opens the board full-page beside the sidebar (same layout as
// DocumentViewerModal).

const CARD_W = 240;
const CARD_H = 128;
const GAP_X = 64;
const GAP_Y = 88;
const PAD = 48;
const COLS = 5;
const ARROW = '#F5843C';
const MIN_SCALE = 0.25;
const MAX_SCALE = 1.75;

const ContactToBuildFlow = ({ steps, darkMode, onStepClick }) => {
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(0.6);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isFullPage, setIsFullPage] = useState(false);
  // Drag state lives in a ref so pointermove doesn't re-render per pixel until commit.
  const dragRef = useRef(null);
  const lastDragMovedRef = useRef(false);
  // Mirrors for the non-passive wheel listener, which can't read stale state.
  const offsetRef = useRef(offset);
  const scaleRef = useRef(scale);
  useEffect(() => { offsetRef.current = offset; }, [offset]);
  useEffect(() => { scaleRef.current = scale; }, [scale]);

  // Full-page mode sits flush against the portal sidebar, matching MainContent's
  // 256px/80px offset (same approach as DocumentViewerModal).
  const sidebar = useContext(SidebarContext);
  const leftOffset = sidebar ? (sidebar.isCollapsed ? 80 : 256) : 0;

  const rows = Math.max(1, Math.ceil(steps.length / COLS));
  const canvasW = PAD * 2 + COLS * CARD_W + (COLS - 1) * GAP_X;
  const canvasH = PAD * 2 + rows * CARD_H + (rows - 1) * GAP_Y;

  // Serpentine positions: even rows read left→right, odd rows right→left, so the
  // arrow at each row's end drops straight down into the next step.
  const positions = useMemo(
    () =>
      steps.map((_, i) => {
        const row = Math.floor(i / COLS);
        const col = i % COLS;
        const visualCol = row % 2 === 0 ? col : COLS - 1 - col;
        return {
          x: PAD + visualCol * (CARD_W + GAP_X),
          y: PAD + row * (CARD_H + GAP_Y),
          row,
        };
      }),
    [steps]
  );

  const fit = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, (vp.clientWidth - 24) / canvasW));
    setScale(s);
    setOffset({ x: (vp.clientWidth - canvasW * s) / 2, y: 12 });
  }, [canvasW]);

  // Re-runs on full-page toggle: React reuses DOM nodes across the mode switch,
  // so the observer (and the wheel listener below) must re-attach to whichever
  // element viewportRef points at now. Also gives the refit on enter/exit.
  useEffect(() => {
    fit();
    const vp = viewportRef.current;
    if (!vp || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(vp);
    return () => ro.disconnect();
  }, [fit, isFullPage]);

  const zoomAt = useCallback((factor) => {
    const vp = viewportRef.current;
    const cx = vp ? vp.clientWidth / 2 : 0;
    const cy = vp ? vp.clientHeight / 2 : 0;
    setScale((prev) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor));
      const ratio = next / prev;
      setOffset((o) => ({ x: cx - (cx - o.x) * ratio, y: cy - (cy - o.y) * ratio }));
      return next;
    });
  }, []);

  // Wheel pans the board vertically (clamped to the canvas). At either end the
  // event is left alone so the page keeps scrolling — needs passive: false to
  // be able to preventDefault when the board does consume it.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e) => {
      const viewH = vp.clientHeight;
      const contentH = canvasH * scaleRef.current;
      const minY = Math.min(12, viewH - contentH - 12);
      const next = Math.max(minY, Math.min(12, offsetRef.current.y - e.deltaY));
      if (next === offsetRef.current.y) return;
      e.preventDefault();
      setOffset((o) => ({ ...o, y: next }));
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [canvasH, isFullPage]);

  // Full-page mode: Esc closes, page scroll locks behind it.
  useEffect(() => {
    if (!isFullPage) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullPage(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullPage]);

  const handlePointerDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: offset, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    if (d.moved) setOffset({ x: d.origin.x + dx, y: d.origin.y + dy });
  };
  const handlePointerUp = () => {
    // Keep moved flag briefly so the card click handler can tell a drag from a tap.
    const d = dragRef.current;
    dragRef.current = null;
    lastDragMovedRef.current = d ? d.moved : false;
  };

  const handleStepClick = () => {
    if (lastDragMovedRef.current) return;
    if (onStepClick) onStepClick();
  };

  const ctrlBtn = `p-1.5 rounded-md transition-colors ${
    darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
  }`;

  // Arrow segments between consecutive steps: horizontal within a row (direction
  // depends on the serpentine), vertical at row breaks.
  const arrows = positions.slice(0, -1).map((from, i) => {
    const to = positions[i + 1];
    if (from.row === to.row) {
      const leftToRight = to.x > from.x;
      const y = from.y + CARD_H / 2;
      const x1 = leftToRight ? from.x + CARD_W : from.x;
      const x2 = leftToRight ? to.x - 7 : to.x + CARD_W + 7;
      return <line key={i} x1={x1} y1={y} x2={x2} y2={y} stroke={ARROW} strokeWidth="2" markerEnd="url(#ctb-arrow)" />;
    }
    const x = from.x + CARD_W / 2;
    return (
      <line key={i} x1={x} y1={from.y + CARD_H} x2={x} y2={to.y - 7} stroke={ARROW} strokeWidth="2" markerEnd="url(#ctb-arrow)" />
    );
  });

  const board = (
    <div
      className={`relative overflow-hidden h-full ${
        isFullPage
          ? darkMode ? 'bg-gray-900' : 'bg-gray-50'
          : `rounded-xl border ${darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-gray-50'}`
      }`}
    >
      {/* Controls: Fit · zoom out · % · zoom in · expand / collapse */}
      <div
        className={`absolute top-3 right-3 z-10 flex items-center gap-1 rounded-lg border px-1 py-0.5 shadow-sm ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        <button
          type="button"
          onClick={fit}
          className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
            darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
          }`}
          title="Fit to view"
        >
          Fit
        </button>
        <span className={`w-px h-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
        <button type="button" onClick={() => zoomAt(1 / 1.25)} className={ctrlBtn} title="Zoom out" aria-label="Zoom out">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className={`text-[11px] w-10 text-center tabular-nums ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {Math.round(scale * 100)}%
        </span>
        <button type="button" onClick={() => zoomAt(1.25)} className={ctrlBtn} title="Zoom in" aria-label="Zoom in">
          <ZoomIn className="w-4 h-4" />
        </button>
        <span className={`w-px h-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
        <button
          type="button"
          onClick={() => setIsFullPage((v) => !v)}
          className={ctrlBtn}
          title={isFullPage ? 'Exit full page (Esc)' : 'Full page'}
          aria-label={isFullPage ? 'Exit full page' : 'Full page'}
        >
          {isFullPage ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      <div
        ref={viewportRef}
        className={`${isFullPage ? 'h-full' : 'h-[520px]'} touch-none select-none cursor-grab active:cursor-grabbing`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div
          style={{
            width: canvasW,
            height: canvasH,
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: '0 0',
          }}
          className="relative"
        >
          <svg width={canvasW} height={canvasH} className="absolute inset-0 pointer-events-none">
            <defs>
              <marker id="ctb-arrow" markerWidth="9" markerHeight="9" refX="7.4" refY="3.2" orient="auto">
                <path d="M0,0 L7.4,3.2 L0,6.4 Z" fill={ARROW} />
              </marker>
            </defs>
            {arrows}
          </svg>

          {steps.map((step, i) => (
            <div
              key={i}
              onClick={handleStepClick}
              style={{ left: positions[i].x, top: positions[i].y, width: CARD_W, height: CARD_H }}
              className={`absolute rounded-xl border p-3 overflow-hidden cursor-pointer transition-shadow hover:shadow-md ${
                darkMode ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-2">
                <span
                  className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${
                    darkMode ? 'bg-gray-100 text-gray-900' : 'bg-gray-900 text-white'
                  }`}
                >
                  {i + 1}
                </span>
                <span className={`text-xs font-bold leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {step.action}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {String(step.assignedTo || '')
                  .split('/')
                  .map((role) => role.trim())
                  .filter(Boolean)
                  .map((role, r) => (
                    <span
                      key={r}
                      className={`px-1.5 py-0.5 rounded-full border text-[9px] font-semibold ${
                        darkMode
                          ? 'border-red-900/60 bg-red-900/30 text-red-300'
                          : 'border-red-100 bg-red-50 text-red-600'
                      }`}
                    >
                      {role}
                    </span>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className={`absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] px-2 py-0.5 rounded-full pointer-events-none ${
          darkMode ? 'bg-gray-800/90 text-gray-400' : 'bg-white/90 text-gray-500'
        }`}
      >
        Scroll or drag to pan · click a step to open the SOP
      </div>
    </div>
  );

  if (isFullPage) {
    return (
      <>
        {/* Placeholder keeps the page layout from collapsing while the board floats. */}
        <div className="h-[520px]" />
        <div className="fixed inset-y-0 right-0 z-[100] transition-[left] duration-300" style={{ left: leftOffset }}>
          {board}
        </div>
      </>
    );
  }

  return board;
};

export default ContactToBuildFlow;
